import React, { useState, useEffect } from 'react';
import { 
  Container, Row, Col, Card, Spinner, Alert, ButtonGroup, Button, 
  Form, InputGroup, Table, Badge, Modal 
} from 'react-bootstrap';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { 
  FaUsers, FaChartLine, FaSearch, FaClock, 
  FaLayerGroup, FaEye, FaChartPie, FaHistory
} from 'react-icons/fa';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useAdminGuard } from '../../../hooks/useAdminGuard';

const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:4004').replace(/\/$/, '');

const ActivityDashboardMejorado = () => {
  useAdminGuard(3); // Requiere AdminLevel 3

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('7');
  const [dashboardData, setDashboardData] = useState(null);
  const [loadTime, setLoadTime] = useState(null);
  const [isCached, setIsCached] = useState(false);
  
  // Estados para búsqueda
  const [searchLegajo, setSearchLegajo] = useState('');
  const [userLogs, setUserLogs] = useState([]);
  const [loadingUser, setLoadingUser] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);

  const fetchDashboardData = async (days = 7) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/activity-logs/dashboard?days=${days}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}`);
      }

      const data = await response.json();
      setDashboardData(data.data);
      setLoadTime(data.loadTime);
      setIsCached(data.cached);
      setError(null);
    } catch (err) {
      console.error('Error fetching dashboard:', err);
      setError('Error al cargar el dashboard. Verifique que la tabla user_activity_logs exista.');
      setDashboardData(null);
    } finally {
      setLoading(false);
    }
  };

  const searchByLegajo = async (legajoParam = null) => {
    const legajo = legajoParam || searchLegajo;
    if (!legajo || !legajo.toString().trim()) return;

    setLoadingUser(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/activity-logs?user_legajo=${legajo}&limit=100`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (!response.ok) throw new Error('Error al buscar logs');

      const data = await response.json();
      setUserLogs(data.data || []);
      
      // Calcular métricas del usuario
      if (data.data && data.data.length > 0) {
        calculateUserMetrics(data.data, legajo);
      }
    } catch (err) {
      console.error('Error buscando logs:', err);
      Alert('Error al buscar actividad del usuario');
    } finally {
      setLoadingUser(false);
    }
  };

  const calculateUserMetrics = (logs, legajo) => {
    const acciones = {};
    const porDia = {};
    const porHora = {};

    logs.forEach(log => {
      // Contar acciones
      acciones[log.action] = (acciones[log.action] || 0) + 1;

      // Por día
      const fecha = format(new Date(log.created_at), 'yyyy-MM-dd');
      porDia[fecha] = (porDia[fecha] || 0) + 1;

      // Por hora
      const hora = new Date(log.created_at).getHours();
      porHora[hora] = (porHora[hora] || 0) + 1;
    });

    setSelectedUser({
      legajo: legajo,
      nombre: logs[0]?.user_name || 'Usuario',
      totalAcciones: logs.length,
      acciones: Object.entries(acciones).map(([k, v]) => ({ accion: k, total: v })).sort((a, b) => b.total - a.total),
      porDia: Object.entries(porDia).map(([k, v]) => ({ fecha: k, total: v })).sort((a, b) => a.fecha.localeCompare(b.fecha)),
      porHora: Object.entries(porHora).map(([k, v]) => ({ hora: `${k}:00`, total: v })).sort((a, b) => parseInt(a.hora) - parseInt(b.hora)),
      ultimaActividad: logs[0]?.created_at
    });
    
    setShowUserModal(true);
  };

  useEffect(() => {
    fetchDashboardData(timeRange);
  }, [timeRange]);

  const COLORS = ['#667eea', '#00D9FF', '#FEB019', '#00E396', '#FF4560', '#775DD0'];

  if (loading) {
    return (
      <Container className="my-5 text-center">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Cargando dashboard...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="my-5">
        <Alert variant="danger">{error}</Alert>
        <Alert variant="info">
          <strong>Nota:</strong> Si la tabla user_activity_logs no existe, los logs no se están guardando.
        </Alert>
      </Container>
    );
  }

  const totalAcciones = dashboardData?.userMetrics?.total_acciones || 0;
  const usuariosActivos = dashboardData?.userMetrics?.usuarios_unicos || 0;
  const promedioAcciones = dashboardData?.userMetrics?.promedio_acciones_por_usuario || 0;

  return (
    <Container fluid className="py-4" style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      {/* Header con búsqueda */}
      <Row className="mb-4">
        <Col lg={6}>
          <h2 className="mb-2" style={{ color: '#2c3e50', fontWeight: '600' }}>
            <FaChartLine className="me-3" style={{ color: '#667eea' }} />
            Dashboard de Actividades
          </h2>
          <p className="text-muted mb-0">Monitoreo y análisis de actividad del sistema</p>
        </Col>
        <Col lg={3} className="d-flex align-items-center">
          <ButtonGroup className="w-100">
            {['7', '15', '30'].map(days => (
              <Button 
                key={days}
                variant={timeRange === days ? 'primary' : 'outline-secondary'}
                onClick={() => setTimeRange(days)}
                style={{ fontWeight: timeRange === days ? '600' : '400' }}
              >
                {days} días
              </Button>
            ))}
          </ButtonGroup>
        </Col>
        <Col lg={3}>
          <InputGroup>
            <Form.Control
              type="text"
              placeholder="Buscar por legajo..."
              value={searchLegajo}
              onChange={(e) => setSearchLegajo(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && searchByLegajo()}
            />
            <Button variant="primary" onClick={searchByLegajo} disabled={loadingUser}>
              {loadingUser ? <Spinner size="sm" animation="border" /> : <FaSearch />}
            </Button>
          </InputGroup>
        </Col>
      </Row>

      {/* Indicador de cache y tiempo de carga */}
      {(isCached || loadTime) && (
        <Row className="mb-3">
          <Col>
            <div className="d-flex gap-2 align-items-center">
              {isCached && (
                <Badge bg="success" className="d-flex align-items-center gap-1">
                  <FaClock size={12} />
                  Datos en caché (actualiza cada 5 min)
                </Badge>
              )}
              {loadTime && (
                <Badge bg="info" className="d-flex align-items-center gap-1">
                  <FaHistory size={12} />
                  Cargado en {loadTime}ms
                </Badge>
              )}
            </div>
          </Col>
        </Row>
      )}

      {/* Cards de métricas principales */}
      <Row className="mb-4">\n        <Col lg={3} md={6} className="mb-3">
          <Card className="border-0 shadow-sm h-100" style={{ borderLeft: '4px solid #667eea' }}>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <p className="text-muted mb-1" style={{ fontSize: '0.85rem', fontWeight: '500' }}>
                    Total Acciones
                  </p>
                  <h2 className="mb-0" style={{ color: '#667eea', fontWeight: '700' }}>
                    {totalAcciones.toLocaleString()}
                  </h2>
                </div>
                <div className="p-3 rounded-circle" style={{ backgroundColor: 'rgba(102, 126, 234, 0.1)' }}>
                  <FaChartLine size={28} style={{ color: '#667eea' }} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={3} md={6} className="mb-3">
          <Card className="border-0 shadow-sm h-100" style={{ borderLeft: '4px solid #00D9FF' }}>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <p className="text-muted mb-1" style={{ fontSize: '0.85rem', fontWeight: '500' }}>
                    Usuarios Activos
                  </p>
                  <h2 className="mb-0" style={{ color: '#00D9FF', fontWeight: '700' }}>
                    {usuariosActivos}
                  </h2>
                </div>
                <div className="p-3 rounded-circle" style={{ backgroundColor: 'rgba(0, 217, 255, 0.1)' }}>
                  <FaUsers size={28} style={{ color: '#00D9FF' }} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={3} md={6} className="mb-3">
          <Card className="border-0 shadow-sm h-100" style={{ borderLeft: '4px solid #FEB019' }}>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <p className="text-muted mb-1" style={{ fontSize: '0.85rem', fontWeight: '500' }}>
                    Promedio / Usuario
                  </p>
                  <h2 className="mb-0" style={{ color: '#FEB019', fontWeight: '700' }}>
                    {promedioAcciones}
                  </h2>
                </div>
                <div className="p-3 rounded-circle" style={{ backgroundColor: 'rgba(254, 176, 25, 0.1)' }}>
                  <FaChartPie size={28} style={{ color: '#FEB019' }} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={3} md={6} className="mb-3">
          <Card className="border-0 shadow-sm h-100" style={{ borderLeft: '4px solid #00E396' }}>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <p className="text-muted mb-1" style={{ fontSize: '0.85rem', fontWeight: '500' }}>
                    Período Actual
                  </p>
                  <h2 className="mb-0" style={{ color: '#00E396', fontWeight: '700' }}>
                    {timeRange} días
                  </h2>
                </div>
                <div className="p-3 rounded-circle" style={{ backgroundColor: 'rgba(0, 227, 150, 0.1)' }}>
                  <FaClock size={28} style={{ color: '#00E396' }} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Gráficos principales */}
      <Row className="mb-4">
        <Col lg={8} className="mb-3">
          <Card className="border-0 shadow-sm h-100">
            <Card.Header className="bg-white border-0 py-3">
              <h5 className="mb-0" style={{ color: '#2c3e50', fontWeight: '600' }}>
                <FaChartLine className="me-2 text-primary" />
                Tendencia de Actividad
              </h5>
            </Card.Header>
            <Card.Body style={{ padding: '1rem' }}>
              {dashboardData?.activityByDay && dashboardData.activityByDay.length > 0 ? (
                <div style={{ width: '100%', height: '400px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={dashboardData.activityByDay} margin={{ top: 15, right: 30, left: 10, bottom: 25 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis 
                        dataKey="fecha" 
                        tickFormatter={(date) => format(new Date(date), 'dd/MM')}
                        style={{ fontSize: '12px' }}
                      />
                      <YAxis style={{ fontSize: '12px' }} />
                      <Tooltip 
                        labelFormatter={(date) => format(new Date(date), 'dd/MM/yyyy')}
                        contentStyle={{ borderRadius: '8px', border: '1px solid #ddd' }}
                      />
                      <Legend wrapperStyle={{ paddingTop: '10px' }} />
                      <Line 
                        type="monotone" 
                        dataKey="total" 
                        stroke="#667eea" 
                        strokeWidth={3}
                        name="Actividades"
                        dot={{ fill: '#667eea', r: 5 }}
                        activeDot={{ r: 7 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="text-center py-5 text-muted">
                  <FaChartLine size={40} className="mb-3 opacity-25" />
                  <p>No hay datos de actividad en este período</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col lg={4} className="mb-3">
          <Card className="border-0 shadow-sm h-100">
            <Card.Header className="bg-white border-0 py-3">
              <h5 className="mb-0" style={{ color: '#2c3e50', fontWeight: '600' }}>
                <FaLayerGroup className="me-2 text-primary" />
                Por Categoría
              </h5>
            </Card.Header>
            <Card.Body style={{ padding: '1rem', overflow: 'hidden', height: '350px' }}>
              {dashboardData?.actionCategories && dashboardData.actionCategories.length > 0 ? (
                <div style={{ width: '100%', height: '100%' }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={dashboardData.actionCategories}
                        dataKey="total"
                        nameKey="categoria"
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        labelLine={{ stroke: '#ccc', strokeWidth: 1 }}
                      >
                        {dashboardData.actionCategories.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ borderRadius: '8px', border: '1px solid #ddd' }}
                        formatter={(value, name) => [`${value} acciones`, name]}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="text-center py-5 text-muted">
                  <FaChartPie size={40} className="mb-3 opacity-25" />
                  <p>No hay categorías disponibles</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Top usuarios y acciones */}
      <Row className="mb-4">
        <Col lg={6} className="mb-3">
          <Card className="border-0 shadow-sm h-100">
            <Card.Header className="bg-white border-0 py-3">
              <h5 className="mb-0" style={{ color: '#2c3e50', fontWeight: '600' }}>
                <FaUsers className="me-2 text-primary" />
                Top 10 Usuarios Más Activos
              </h5>
            </Card.Header>
            <Card.Body style={{ maxHeight: '400px', overflowY: 'auto' }}>
              <Table hover responsive size="sm" className="mb-0">
                <thead style={{ position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 1 }}>
                  <tr>
                    <th style={{ width: '60px' }}>#</th>
                    <th style={{ width: '100px' }}>Legajo</th>
                    <th>Nombre</th>
                    <th className="text-end" style={{ width: '100px' }}>Acciones</th>
                    <th style={{ width: '60px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {(dashboardData?.topUsuarios || []).length > 0 ? (
                    dashboardData.topUsuarios.map((user, idx) => (
                      <tr key={idx}>
                        <td>
                          <Badge bg="primary" pill>{idx + 1}</Badge>
                        </td>
                        <td><strong>{user.user_legajo}</strong></td>
                        <td>{user.nombre || '-'}</td>
                        <td className="text-end">
                          <span className="badge bg-info">{user.total_acciones}</span>
                        </td>
                        <td>
                          <Button 
                            size="sm" 
                            variant="outline-primary"
                            onClick={() => searchByLegajo(user.user_legajo)}
                          >
                            <FaEye />
                          </Button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="text-center text-muted py-4">
                        No hay datos disponibles
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={6} className="mb-3">
          <Card className="border-0 shadow-sm h-100">
            <Card.Header className="bg-white border-0 py-3">
              <h5 className="mb-0" style={{ color: '#2c3e50', fontWeight: '600' }}>
                <FaChartLine className="me-2 text-primary" />
                Top 10 Acciones
              </h5>
            </Card.Header>
            <Card.Body style={{ overflow: 'hidden' }}>
              {dashboardData?.topAcciones && dashboardData.topAcciones.length > 0 ? (
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart data={dashboardData.topAcciones} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis type="number" style={{ fontSize: '12px' }} />
                    <YAxis 
                      dataKey="accion" 
                      type="category" 
                      width={140}
                      style={{ fontSize: '11px' }}
                    />
                    <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #ddd' }} />
                    <Bar dataKey="total" fill="#667eea" radius={[0, 8, 8, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center py-5 text-muted">
                  <FaChartLine size={40} className="mb-3 opacity-25" />
                  <p>No hay acciones registradas</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Actividad por hora y últimas actividades */}
      <Row className="mb-4">
        <Col lg={12} className="mb-3">
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-white border-0 py-3">
              <h5 className="mb-0" style={{ color: '#2c3e50', fontWeight: '600' }}>
                <FaClock className="me-2 text-primary" />
                Actividad por Hora del Día
              </h5>
            </Card.Header>
            <Card.Body style={{ overflow: 'hidden' }}>
              {dashboardData?.activityByHour && dashboardData.activityByHour.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={dashboardData.activityByHour}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis 
                      dataKey="hora" 
                      tickFormatter={(hora) => `${hora}:00`}
                      style={{ fontSize: '12px' }}
                    />
                    <YAxis style={{ fontSize: '12px' }} />
                    <Tooltip 
                      labelFormatter={(hora) => `${hora}:00 hrs`}
                      contentStyle={{ borderRadius: '8px', border: '1px solid #ddd' }}
                    />
                    <Bar dataKey="total" fill="#00D9FF" name="Acciones" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center py-5 text-muted">
                  <FaClock size={40} className="mb-3 opacity-25" />
                  <p>No hay datos de actividad por hora</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Últimas actividades simplificadas */}
      <Row className="mb-4">
        <Col lg={12}>
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-white border-0 py-3">
              <h5 className="mb-0" style={{ color: '#2c3e50', fontWeight: '600' }}>
                <FaHistory className="me-2 text-primary" />
                Últimas Actividades
              </h5>
            </Card.Header>
            <Card.Body style={{ maxHeight: '500px', overflowY: 'auto' }}>
              <Table hover responsive size="sm" className="mb-0">
                <thead style={{ position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 1 }}>
                  <tr>
                    <th style={{ width: '100px' }}>Legajo</th>
                    <th style={{ width: '200px' }}>Nombre</th>
                    <th>Acción</th>
                    <th style={{ width: '160px' }}>Fecha</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboardData?.ultimasActividades && dashboardData.ultimasActividades.length > 0 ? (
                    dashboardData.ultimasActividades.map((log, idx) => (
                      <tr key={idx}>
                        <td><strong>{log.user_legajo}</strong></td>
                        <td>{log.usuario_nombre || '-'}</td>
                        <td>
                          <span className="text-muted" style={{ fontSize: '0.9rem' }}>
                            {log.accion || log.action}
                          </span>
                        </td>
                        <td>
                          <small className="text-muted">
                            {format(new Date(log.created_at), 'dd/MM/yyyy HH:mm', { locale: es })}
                          </small>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="text-center text-muted py-4">
                        No hay actividades recientes
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Modal de detalles del usuario */}
      <Modal show={showUserModal} onHide={() => setShowUserModal(false)} size="lg" centered>
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title style={{ color: '#2c3e50', fontWeight: '600' }}>
            <FaUsers className="me-2 text-primary" />
            Detalle de Actividad
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-2">
          {selectedUser && (
            <>
              <Row className="mb-4">
                <Col md={4}>
                  <Card className="border shadow-sm">
                    <Card.Body className="text-center py-3">
                      <small className="text-muted d-block">Legajo</small>
                      <h4 className="mb-0 mt-1" style={{ color: '#667eea', fontWeight: '700' }}>
                        {selectedUser.legajo}
                      </h4>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="border shadow-sm">
                    <Card.Body className="text-center py-3">
                      <small className="text-muted d-block">Nombre</small>
                      <h5 className="mb-0 mt-1" style={{ fontWeight: '600' }}>
                        {selectedUser.nombre}
                      </h5>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="border shadow-sm">
                    <Card.Body className="text-center py-3">
                      <small className="text-muted d-block">Total Acciones</small>
                      <h4 className="mb-0 mt-1" style={{ color: '#00E396', fontWeight: '700' }}>
                        {selectedUser.totalAcciones}
                      </h4>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <Card className="border-0 shadow-sm mb-3">
                <Card.Header className="bg-light border-0">
                  <h6 className="mb-0" style={{ fontWeight: '600' }}>
                    Top Acciones del Usuario
                  </h6>
                </Card.Header>
                <Card.Body style={{ maxHeight: '250px', overflowY: 'auto' }}>
                  <Table size="sm" hover className="mb-0">
                    <thead>
                      <tr>
                        <th>Acción</th>
                        <th className="text-end" style={{ width: '100px' }}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedUser.acciones.slice(0, 15).map((acc, idx) => (
                        <tr key={idx}>
                          <td>{acc.accion}</td>
                          <td className="text-end">
                            <Badge bg="primary" pill>{acc.total}</Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>

              <Card className="border-0 shadow-sm">
                <Card.Header className="bg-light border-0">
                  <h6 className="mb-0" style={{ fontWeight: '600' }}>
                    Actividad por Día
                  </h6>
                </Card.Header>
                <Card.Body style={{ overflow: 'hidden' }}>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={selectedUser.porDia}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis 
                        dataKey="fecha" 
                        tickFormatter={(fecha) => format(new Date(fecha), 'dd/MM')}
                        style={{ fontSize: '11px' }}
                      />
                      <YAxis style={{ fontSize: '11px' }} />
                      <Tooltip 
                        labelFormatter={(fecha) => format(new Date(fecha), 'dd/MM/yyyy')}
                        contentStyle={{ borderRadius: '8px' }}
                      />
                      <Bar dataKey="total" fill="#667eea" radius={[8, 8, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Card.Body>
              </Card>
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button variant="secondary" onClick={() => setShowUserModal(false)}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default ActivityDashboardMejorado;
